import { Link, useParams } from 'react-router-dom';
import { publicEvents } from '@/i18n/dictionaries/publicEvents';
import { useTranslation } from '@/i18n/useTranslation';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import { PUBLIC_EVENT_STATUS } from '@/features/publicsite/events/eventStatus';
import { EventDetailHeader } from '@/features/publicsite/events/EventDetailHeader';
import { EventDetailSection } from '@/features/publicsite/events/EventDetailSection';
import { EventFeedbackForm } from '@/features/publicsite/events/EventFeedbackForm';
import { EventFiles } from '@/features/publicsite/events/EventFiles';
import { eventTextLang } from '@/features/publicsite/events/eventLanguage';
import { EventRatings } from '@/features/publicsite/events/EventRatings';
import { EventRegistration } from '@/features/publicsite/events/EventRegistration';
import { useEventDetail } from '@/features/publicsite/events/useEventDetail';
import { useEventFeedbackForm } from '@/features/publicsite/events/useEventFeedbackForm';
import { useEventParticipation } from '@/features/publicsite/events/useEventParticipation';
import { useEventRegistration } from '@/features/publicsite/events/useEventRegistration';
import type { EventTranslatedField } from '@/features/publicsite/types/events/EventTranslatedField';

/**
 * The card's text sections, in the order the page shows them. Agenda and program each under its own
 * heading: the page printed `program || agenda` under "Program", so an event that had both lost its
 * agenda (tor.md §6.11 names it).
 */
const TEXT_SECTIONS = [
  'objectives',
  'agenda',
  'program',
  'speakers',
  'partners',
  'acceptedPapers',
] as const;

export function EventDetailPage() {
  // The route param is the event's slug for every link the site now hands out; the API accepts a
  // bare id too (GET /api/v1/public/events/{idOrSlug}), so a link shared before slugs existed
  // still resolves without any parsing on this end.
  const { slug: param = '' } = useParams();
  const { t, language } = useTranslation(publicEvents);
  const { event, feedback, missing, refreshFeedback } = useEventDetail(param);
  const { participation, setParticipation, refresh } = useEventParticipation(event);
  const { registerStatus, register, cancel } = useEventRegistration(refresh);
  const feedbackForm = useEventFeedbackForm();

  usePublicPageTitle(missing ? t('notFound') : (event?.title ?? null));

  if (missing) {
    return (
      <section className="container section">
        <h1>{t('notFound')}</h1>
        <p>
          <Link to="/evenimente">{t('backToCalendar')}</Link>
        </p>
      </section>
    );
  }
  if (!event) {
    return (
      <section className="container section">
        <p className="empty-state">{t('loading')}</p>
      </section>
    );
  }

  // PUBLISHED alone kept the button on events that had already happened, since nothing moves an event
  // to CONCLUDED on its own; `past` is the server's rule, the one register() refuses by.
  const open = event.status === PUBLIC_EVENT_STATUS.published && !event.past;
  const lang = (field: EventTranslatedField) => eventTextLang(event, field, language);

  // The accepted evaluation is recorded locally first, then the average re-read so it counts it.
  const submitFeedback = () =>
    feedbackForm.submit(event.id, (accepted) => {
      setParticipation(accepted);
      refreshFeedback();
    });

  return (
    <section className="container section">
      <EventDetailHeader event={event} />

      {/* One card, not one per heading. Program and Participare are two things a visitor reads in
        sequence — what the event is, then how to attend it — and two bordered boxes side by side
        said they were alternatives to choose between. Speakers, papers and the media archive are
        sections of the same card for the same reason: they are all "about this event". */}
      <div className="event-detail__card">
        {TEXT_SECTIONS.map(
          (field) =>
            event[field] && (
              <EventDetailSection key={field} heading={t(field)} lang={lang(field)}>
                {event[field]}
              </EventDetailSection>
            ),
        )}
        {event.documents.length > 0 && (
          <EventFiles heading={t('documents')} files={event.documents} />
        )}
        {event.archive.length > 0 && (
          <EventFiles heading={t('mediaArchive')} files={event.archive} />
        )}

        <section className="event-detail__section">
          <h2 className="event-detail__section-heading">{t('participation')}</h2>
          {event.eligibility && (
            <>
              <h3 className="event-detail__subheading">{t('eligibility')}</h3>
              <p className="event-detail__prose" lang={lang('eligibility')}>
                {event.eligibility}
              </p>
            </>
          )}
          <EventRegistration
            open={open}
            cancelled={event.status === PUBLIC_EVENT_STATUS.cancelled}
            registered={participation?.registered ?? false}
            mayCancel={participation?.mayCancel ?? false}
            hasMedia={event.archive.length > 0}
            status={registerStatus}
            onRegister={() => void register(event.id)}
            onCancel={() => void cancel(event.id)}
          />
          {/* Outside the open/concluded branches on purpose, and gated on the server's own
            mayLeaveFeedback rather than on this page's `open`. An event whose date has passed
            often still sits in PUBLISHED — reaching CONCLUDED is a workflow step somebody has to
            remember — and that is exactly the participant the evaluation is for. Deciding here
            from `status` showed the form to nobody in that case, while deciding from `registered`
            alone showed it before the event happened, where the server answers 409. */}
          {participation?.mayLeaveFeedback && (
            <EventFeedbackForm
              rating={feedbackForm.rating}
              comments={feedbackForm.comments}
              presentationRating={feedbackForm.presentationRating}
              futureNeeds={feedbackForm.futureNeeds}
              error={feedbackForm.error}
              submitting={feedbackForm.submitting}
              onRatingChange={feedbackForm.setRating}
              onCommentsChange={feedbackForm.setComments}
              onPresentationRatingChange={feedbackForm.setPresentationRating}
              onFutureNeedsChange={feedbackForm.setFutureNeeds}
              onSubmit={() => void submitFeedback()}
            />
          )}
          {participation?.feedbackSubmitted && (
            <p className="page-sub event-detail__status">
              {t('feedbackAlready', { rating: participation.myRating ?? '' })}
            </p>
          )}
          <EventRatings feedback={feedback} />
        </section>
      </div>
    </section>
  );
}
